import React, { useEffect, useState } from 'react';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    if (activeModal) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [activeModal]);
  return <>
      <footer className="bg-ink py-12" data-id="element-38">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center text-center" data-id="element-39">
          <p className="text-cream-light/60 text-sm mb-6 max-w-2xl" data-id="element-40">
            Disclaimer: This product is a digital guide for informational
            purposes only. Results may vary. The information provided is not
            intended as medical advice. Always consult with a healthcare
            professional before starting any new diet or wellness program.
          </p>
          <div className="flex items-center gap-6 mb-6" data-id="element-41">
            <button onClick={() => setActiveModal('privacy')} className="text-cream-light/70 hover:text-white text-sm font-medium underline underline-offset-2 transition-colors" data-id="element-42">
              Privacy Policy
            </button>
            <span className="text-cream-light/30" data-id="element-43">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream-light/70 hover:text-white text-sm font-medium underline underline-offset-2 transition-colors" data-id="element-44">
              Terms of Service
            </button>
          </div>
          <p className="text-cream-light/40 text-sm" data-id="element-45">
            &copy; {new Date().getFullYear()} 14-Day Belly Reset. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* Modal Overlay */}
      {activeModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-46">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-47">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-cream-dark bg-cream-light" data-id="element-48">
              <h2 className="text-xl font-bold text-ink font-serif" data-id="element-49">
                {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
              </h2>
              <button onClick={() => setActiveModal(null)} className="p-2 rounded-full hover:bg-cream-dark transition-colors text-ink/60 hover:text-ink" aria-label="Close" data-id="element-50">
                <XIcon className="w-5 h-5" data-id="element-51" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto px-6 py-6 text-ink/80 text-sm leading-relaxed space-y-4" data-id="element-52">
              {activeModal === 'privacy' ? <PrivacyContent data-id="element-53" /> : <TermsContent data-id="element-54" />}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-cream-dark bg-cream-light" data-id="element-55">
              <button onClick={() => setActiveModal(null)} className="w-full sm:w-auto px-6 py-2.5 bg-sage-dark text-white font-semibold rounded-full hover:bg-sage-dark/90 transition-colors text-sm" data-id="element-56">
                Close
              </button>
            </div>
          </div>
        </div>}
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-ink/50" data-id="element-57">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <h3 className="text-base font-bold text-ink mt-4" data-id="element-58">
        1. Information We Collect
      </h3>
      <p data-id="element-59">
        When you purchase the 14-Day Belly Reset Plan, we collect only the
        information necessary to process your order, including your name, email
        address, and payment details. Payment processing is handled by secure
        third-party providers — we do not store your credit card information.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-60">
        2. How We Use Your Information
      </h3>
      <p data-id="element-61">
        We use your information solely to deliver your digital product, send
        your purchase confirmation, and provide customer support if needed. We
        will not sell, rent, or share your personal information with third
        parties for marketing purposes.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-62">
        3. Cookies &amp; Analytics
      </h3>
      <p data-id="element-63">
        Our website may use cookies and analytics tools to understand how
        visitors interact with our pages. This data is collected anonymously and
        is used to improve the user experience. You can disable cookies in your
        browser settings at any time.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-64">4. Data Security</h3>
      <p data-id="element-65">
        We take reasonable measures to protect your personal information from
        unauthorized access, alteration, or disclosure. However, no method of
        transmission over the internet is 100% secure, and we cannot guarantee
        absolute security.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-66">5. Your Rights</h3>
      <p data-id="element-67">
        You have the right to request access to, correction of, or deletion of
        your personal data at any time. To exercise these rights, please reach
        out through the communication channels provided at the time of purchase.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-68">
        6. Changes to This Policy
      </h3>
      <p data-id="element-69">
        We may update this Privacy Policy from time to time. Any changes will be
        posted on this page with an updated revision date. Your continued use of
        our products constitutes acceptance of the updated policy.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-ink/50" data-id="element-70">
        Last updated:{' '}
        {new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}
      </p>

      <h3 className="text-base font-bold text-ink mt-4" data-id="element-71">
        1. Product Description
      </h3>
      <p data-id="element-72">
        The 14-Day Belly Reset Plan is a digital guide (eBook) designed for
        informational and educational purposes. It includes meal templates,
        grocery lists, habit trackers, and troubleshooting guides. This product
        is not a substitute for professional medical advice, diagnosis, or
        treatment.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-73">2. Digital Delivery</h3>
      <p data-id="element-74">
        Upon successful payment, you will receive instant access to the digital
        product. As this is a digital product delivered immediately, all sales
        are final. No refunds will be issued once the product has been accessed
        or downloaded.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-75">3. Disclaimer of Results</h3>
      <p data-id="element-76">
        Individual results may vary. The information provided in this guide is
        based on general wellness principles and is not guaranteed to produce
        specific outcomes. Always consult with a qualified healthcare
        professional before making changes to your diet or lifestyle, especially
        if you have existing health conditions.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-77">4. Intellectual Property</h3>
      <p data-id="element-78">
        All content within the 14-Day Belly Reset Plan, including text,
        graphics, templates, and design, is protected by copyright. You may not
        reproduce, distribute, or resell any part of this product without prior
        written permission.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-79">
        5. Limitation of Liability
      </h3>
      <p data-id="element-80">
        To the fullest extent permitted by law, we shall not be liable for any
        indirect, incidental, or consequential damages arising from the use of
        this product. Our total liability shall not exceed the purchase price
        paid for the product.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-81">6. Governing Law</h3>
      <p data-id="element-82">
        These terms shall be governed by and construed in accordance with
        applicable laws. Any disputes arising from these terms or the use of
        this product shall be resolved through appropriate legal channels.
      </p>

      <h3 className="text-base font-bold text-ink" data-id="element-83">
        7. Changes to These Terms
      </h3>
      <p data-id="element-84">
        We reserve the right to update these Terms of Service at any time.
        Changes will be effective immediately upon posting. Your continued use
        of the product constitutes acceptance of the revised terms.
      </p>
    </>;
}